/**
 * AdminHorarios.jsx — Gestión de horarios laborales por técnico.
 * v9.2 · Dos pestañas:
 *   Tab 1 "Configurar Turnos": CRUD de plantillas (nombre, días semana, horario)
 *   Tab 2 "Asignar Técnicos": Selección múltiple + asignar plantilla existente
 */
import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Clock, Users, Save, Loader2, Plus, Trash2, X, Search,
  CheckSquare, Square, Settings2, UserCheck, Pencil,
} from "lucide-react";

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const SUGGESTED_COLORS = ["#F59E0B", "#3B82F6", "#7C3AED", "#059669", "#DC2626", "#EC4899", "#0EA5E9", "#64748B"];

export default function AdminHorarios() {
  const [tab, setTab] = useState("turnos"); // turnos | tecnicos

  return (
    <Layout>
      <div className="space-y-5" data-testid="admin-horarios-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>Admin · Horarios</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Horarios laborales</h1>
            <p className="mt-1 text-sm text-gray-500">Configura turnos y asígnalos a los técnicos.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-gray-100 w-fit" data-testid="horarios-tabs">
          <button
            onClick={() => setTab("turnos")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition ${
              tab === "turnos" ? "bg-white shadow-sm text-[#0A3747]" : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-turnos"
          >
            <Settings2 size={14} />
            Configurar Turnos
          </button>
          <button
            onClick={() => setTab("tecnicos")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition ${
              tab === "tecnicos" ? "bg-white shadow-sm text-[#0A3747]" : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-tecnicos"
          >
            <UserCheck size={14} />
            Asignar Técnicos
          </button>
        </div>

        {tab === "turnos" && <ShiftTemplatesTab />}
        {tab === "tecnicos" && <AssignTechniciansTab />}
      </div>
    </Layout>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 1: Configurar Turnos (plantillas)
   ═══════════════════════════════════════════════════════════════════ */
function ShiftTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | template obj

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/shifts/templates");
      setTemplates(data.items || []);
    } catch { toast.error("Error cargando plantillas"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla? Se desvinculará de los técnicos asignados.")) return;
    try {
      await api.delete(`/v1/shifts/templates/${id}`);
      toast.success("Plantilla eliminada");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-600">
          {templates.length} plantilla{templates.length !== 1 ? "s" : ""} configurada{templates.length !== 1 ? "s" : ""}
        </div>
        <button
          onClick={() => setEditing("new")}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white transition hover:opacity-90"
          style={{ background: "#0A3747" }}
          data-testid="new-template-btn"
        >
          <Plus size={14} /> Nuevo Turno
        </button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
      ) : templates.length === 0 ? (
        <div className="neu-card py-16 text-center">
          <Clock size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No hay turnos configurados.</p>
          <p className="text-xs text-gray-400 mt-1">Crea una plantilla para empezar a asignar horarios.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map(t => (
            <div key={t.id} className="neu-card p-4 relative group" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-3 rounded-full flex-shrink-0" style={{ background: t.color, height: Math.max(40, (t.time_blocks?.length || 1) * 24) }} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm" style={{ color: "#0A3747" }}>{t.name}</div>
                  <div className="space-y-0.5 mt-0.5">
                    {(t.time_blocks || []).map((b, i) => (
                      <div key={i} className="text-xs text-gray-500 font-mono">
                        {b.start?.slice(0, 5)} – {b.end?.slice(0, 5)}
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {DAY_LABELS.map((label, i) => {
                      const active = (t.days_of_week || []).includes(i);
                      return (
                        <span key={i}
                          className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            active ? "text-white" : "text-gray-300 bg-gray-50"
                          }`}
                          style={active ? { background: t.color } : {}}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                  {t.notes && <div className="text-[10px] text-gray-400 mt-1.5 truncate">{t.notes}</div>}
                </div>
                <div className="flex-shrink-0 flex items-center gap-0.5">
                  <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                    {t.assigned_count || 0}
                  </span>
                </div>
              </div>
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => setEditing(t)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600" data-testid={`edit-template-${t.id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={`delete-template-${t.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditor
          template={editing === "new" ? null : editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

/* ─── Template Editor Modal (multi-block) ──────────────────────── */
function TemplateEditor({ template, onClose }) {
  const isNew = !template;
  const [name, setName] = useState(template?.name || "");
  const [days, setDays] = useState(template?.days_of_week || [0, 1, 2, 3, 4]);
  const [blocks, setBlocks] = useState(() => {
    if (template?.time_blocks?.length) {
      return template.time_blocks.map(b => ({ start: b.start?.slice(0,5) || "07:00", end: b.end?.slice(0,5) || "15:00" }));
    }
    return [{ start: "07:00", end: "15:00" }];
  });
  const [color, setColor] = useState(template?.color || "#3B82F6");
  const [notes, setNotes] = useState(template?.notes || "");
  const [branchId, setBranchId] = useState(template?.branch_id || "");
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/v1/config/branches").then(r => setBranches(r.data?.items || r.data || [])).catch(() => {});
  }, []);

  const toggleDay = (d) => {
    setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  };
  const updateBlock = (idx, field, val) => {
    setBlocks(prev => prev.map((b, i) => i === idx ? { ...b, [field]: val } : b));
  };
  const addBlock = () => setBlocks(prev => [...prev, { start: "08:00", end: "12:00" }]);
  const removeBlock = (idx) => setBlocks(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!name.trim()) { toast.error("Ingresa un nombre"); return; }
    if (!branchId) { toast.error("La sucursal es obligatoria"); return; }
    if (days.length === 0) { toast.error("Selecciona al menos un día"); return; }
    if (blocks.length === 0) { toast.error("Agrega al menos un bloque horario"); return; }
    setSaving(true);
    try {
      const payload = { name, days_of_week: days, time_blocks: blocks, color, notes, branch_id: branchId };
      if (isNew) {
        await api.post("/v1/shifts/templates", payload);
        toast.success("Plantilla creada");
      } else {
        await api.put(`/v1/shifts/templates/${template.id}`, payload);
        toast.success("Plantilla actualizada");
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="template-editor-modal">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b" style={{ background: "#FBFCE0" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#6B7000" }}>
              {isNew ? "Nuevo turno" : "Editar turno"}
            </div>
            <h2 className="text-lg font-bold mt-1" style={{ color: "#0A3747" }}>
              {isNew ? "Crear plantilla" : template.name}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="template-editor-close"><X size={22} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Name */}
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Nombre del turno</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Ej: Mañana, Tarde, Noche, Guardia..."
                   className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 outline-none"
                   data-testid="template-name" />
          </label>

          {/* Sucursal (obligatoria) */}
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Sucursal *</span>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
                    className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 outline-none"
                    data-testid="template-branch">
              <option value="">-- Seleccionar sucursal --</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name || b.nomenclatura || b.id}</option>
              ))}
            </select>
          </label>

          {/* Days of week */}
          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500 block mb-1.5">Días de la semana</span>
            <div className="flex gap-1.5">
              {DAY_LABELS.map((label, i) => {
                const active = days.includes(i);
                return (
                  <button key={i} onClick={() => toggleDay(i)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition border ${
                      active ? "text-white border-transparent" : "text-gray-400 border-gray-200 bg-white hover:border-gray-300"
                    }`}
                    style={active ? { background: color } : {}}
                    data-testid={`template-day-${i}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time blocks */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Bloques horarios</span>
              <button onClick={addBlock} className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg border hover:bg-gray-50 transition" data-testid="add-block-btn">
                <Plus size={11} /> Agregar bloque
              </button>
            </div>
            <div className="space-y-2">
              {blocks.map((b, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100" data-testid={`block-${idx}`}>
                  <Clock size={13} className="text-gray-400 flex-shrink-0" />
                  <input type="time" value={b.start} onChange={(e) => updateBlock(idx, "start", e.target.value)}
                         className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-mono focus:ring-2 focus:ring-sky-200 outline-none"
                         data-testid={`block-start-${idx}`} />
                  <span className="text-gray-400 text-xs">a</span>
                  <input type="time" value={b.end} onChange={(e) => updateBlock(idx, "end", e.target.value)}
                         className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-mono focus:ring-2 focus:ring-sky-200 outline-none"
                         data-testid={`block-end-${idx}`} />
                  {blocks.length > 1 && (
                    <button onClick={() => removeBlock(idx)} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 transition" data-testid={`block-remove-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Color */}
          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500 block mb-1.5">Color</span>
            <div className="flex gap-2">
              {SUGGESTED_COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full transition ring-offset-2 ${color === c ? "ring-2 ring-gray-400" : "hover:scale-110"}`}
                  style={{ background: c }}
                  data-testid={`template-color-${c}`}
                />
              ))}
            </div>
          </div>

          {/* Notes */}
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Notas (opcional)</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="Descripción adicional..."
                   className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-[11px] focus:ring-2 focus:ring-sky-200 outline-none"
                   data-testid="template-notes" />
          </label>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold text-gray-600 rounded-lg border hover:bg-gray-100 transition" data-testid="template-cancel">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-xs font-bold text-white transition hover:opacity-90 disabled:opacity-40"
            style={{ background: "#0A3747" }}
            data-testid="template-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isNew ? "Crear" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 2: Asignar Técnicos
   ═══════════════════════════════════════════════════════════════════ */
function AssignTechniciansTab() {
  const [techs, setTechs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterZone, setFilterZone] = useState("");
  const [filterTemplate, setFilterTemplate] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [techRes, tplRes, asgRes, brRes, znRes] = await Promise.allSettled([
        api.get("/users/schedules/summary"),
        api.get("/v1/shifts/templates"),
        api.get("/v1/shifts/assignments"),
        api.get("/v1/config/branches"),
        api.get("/admin/zonas"),
      ]);
      if (techRes.status === "fulfilled") setTechs(techRes.value.data.items || []);
      if (tplRes.status === "fulfilled") setTemplates(tplRes.value.data.items || []);
      if (asgRes.status === "fulfilled") setAssignments(asgRes.value.data.items || []);
      if (brRes.status === "fulfilled") setBranches(brRes.value.data?.items || brRes.value.data || []);
      if (znRes.status === "fulfilled") setZones(znRes.value.data || []);
    } catch { toast.error("Error cargando datos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Map technician_id → list of assigned templates
  const techAssignments = useMemo(() => {
    const map = {};
    for (const a of assignments) {
      if (!map[a.technician_id]) map[a.technician_id] = [];
      map[a.technician_id].push(a);
    }
    return map;
  }, [assignments]);

  const filtered = useMemo(() => {
    let list = techs;
    if (q.trim()) {
      const query = q.toLowerCase();
      list = list.filter(t =>
        (t.name || "").toLowerCase().includes(query) ||
        (t.email || "").toLowerCase().includes(query)
      );
    }
    if (filterBranch) {
      list = list.filter(t => String(t.branch_id) === filterBranch || String(t.branch_name || "").toLowerCase().includes(filterBranch.toLowerCase()));
    }
    if (filterZone) {
      // v101.3.0 · El técnico puede tener varias zonas
      list = list.filter(t => String(t.zone_id) === filterZone || (t.zone_ids || []).map(String).includes(filterZone) || (t.zone_name || "").toLowerCase().includes(filterZone.toLowerCase()));
    }
    if (filterTemplate) {
      list = list.filter(t => {
        const assigns = techAssignments[t.user_id] || [];
        if (filterTemplate === "_none") return assigns.length === 0;
        return assigns.some(a => a.shift_template_id === filterTemplate);
      });
    }
    return list;
  }, [techs, q, filterBranch, filterZone, filterTemplate, techAssignments]);

  const toggleCheck = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (checked.size === filtered.length && filtered.length > 0) setChecked(new Set());
    else setChecked(new Set(filtered.map(t => t.user_id)));
  };

  const handleAssign = async () => {
    if (checked.size === 0) { toast.error("Selecciona al menos un técnico"); return; }
    if (!selectedTemplate) { toast.error("Selecciona un turno"); return; }
    setSaving(true);
    try {
      const r = await api.post("/v1/shifts/assignments/bulk", {
        technician_ids: Array.from(checked),
        shift_template_id: selectedTemplate,
      });
      const tpl = templates.find(t => t.id === selectedTemplate);
      toast.success(`Turno "${tpl?.name}" asignado a ${r.data.assigned} técnico(s)`);
      setChecked(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al asignar");
    } finally { setSaving(false); }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await api.delete(`/v1/shifts/assignments/${assignmentId}`);
      toast.success("Turno desvinculado");
      load();
    } catch { toast.error("Error al desvincular"); }
  };

  return (
    <>
      {/* Action bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Buscar técnico..."
                 className="pl-9 w-full text-xs border border-gray-300 rounded-lg px-2.5 py-2 bg-white outline-none"
                 data-testid="assign-search" />
        </div>
        {/* Filtros v92.5.1 */}
        <select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-2 bg-white outline-none" data-testid="filter-branch">
          <option value="">Todas las sucursales</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name || b.nomenclatura}</option>)}
        </select>
        <select value={filterZone} onChange={(e) => setFilterZone(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-2 bg-white outline-none" data-testid="filter-zone">
          <option value="">Todas las zonas</option>
          {zones.map(z => <option key={z.id || z.name} value={z.id || z.name}>{z.name || z.id}</option>)}
        </select>
        <select value={filterTemplate} onChange={(e) => setFilterTemplate(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-2 bg-white outline-none" data-testid="filter-template">
          <option value="">Todas las jornadas</option>
          <option value="_none">Sin jornada</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        {checked.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 border border-blue-200 animate-in fade-in" data-testid="assign-action-bar">
            <CheckSquare size={14} className="text-blue-600" />
            <span className="text-xs font-semibold text-blue-800">{checked.size} seleccionado{checked.size !== 1 ? "s" : ""}</span>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="text-xs border border-blue-200 rounded-lg px-2 py-1.5 bg-white outline-none"
              data-testid="assign-template-select"
            >
              <option value="">Seleccionar turno...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.start_time?.slice(0,5)}–{t.end_time?.slice(0,5)})
                </option>
              ))}
            </select>
            <button
              onClick={handleAssign}
              disabled={saving || !selectedTemplate}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition hover:opacity-90 disabled:opacity-40"
              style={{ background: "#0A3747" }}
              data-testid="assign-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={12} />}
              Asignar
            </button>
            <button onClick={() => setChecked(new Set())} className="text-[10px] text-gray-500 hover:text-gray-700 ml-1">Limpiar</button>
          </div>
        )}
      </div>

      {templates.length === 0 && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
          No hay turnos configurados. Ve a la pestaña <strong>"Configurar Turnos"</strong> para crear uno primero.
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
      ) : (
        <div className="neu-card overflow-hidden">
          <table className="w-full text-sm neu-table">
            <thead><tr>
              <th className="px-3 py-2 w-10">
                <button onClick={selectAll} data-testid="assign-select-all">
                  {checked.size > 0 && checked.size === filtered.length
                    ? <CheckSquare size={16} className="text-blue-600" />
                    : <Square size={16} className="text-gray-400" />}
                </button>
              </th>
              <th className="px-3 py-2 text-left">Técnico</th>
              <th className="px-3 py-2 text-left">Turno(s) asignado(s)</th>
              <th className="px-3 py-2 text-center w-24">Días lab.</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="4" className="py-10 text-center text-gray-500">Sin técnicos.</td></tr>
              ) : filtered.map(t => {
                const isChecked = checked.has(t.user_id);
                const myAssignments = techAssignments[t.user_id] || [];
                return (
                  <tr key={t.user_id}
                      className={`border-t transition ${isChecked ? "bg-blue-50/60" : "hover:bg-gray-50"}`}
                      data-testid={`tech-row-${t.user_id}`}>
                    <td className="px-3 py-2">
                      <button onClick={() => toggleCheck(t.user_id)} data-testid={`check-${t.user_id}`}>
                        {isChecked
                          ? <CheckSquare size={16} className="text-blue-600" />
                          : <Square size={16} className="text-gray-300 hover:text-gray-500" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 cursor-pointer" onClick={() => toggleCheck(t.user_id)}>
                      <div className="font-medium text-sm" style={{ color: "#0A3747" }}>{t.name}</div>
                      <div className="text-[11px] text-gray-500">{t.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      {myAssignments.length === 0 ? (
                        <span className="text-[10px] text-gray-400 uppercase tracking-widest">Sin turno</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {myAssignments.map(a => (
                            <span key={a.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white group/badge"
                                  style={{ background: a.color || "#64748B" }}
                                  data-testid={`assignment-badge-${a.id}`}>
                              {a.shift_name}
                              <span className="text-[8px] font-mono opacity-80">
                                {(a.time_blocks || []).map(b => `${b.start?.slice(0,5)}-${b.end?.slice(0,5)}`).join(" / ")}
                              </span>
                              <button onClick={(e) => { e.stopPropagation(); handleRemoveAssignment(a.id); }}
                                      className="ml-0.5 opacity-60 hover:opacity-100" data-testid={`remove-assignment-${a.id}`}>
                                <X size={10} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs font-semibold ${t.working_days > 0 ? "text-emerald-700" : "text-gray-400"}`}>
                        {t.working_days || 0} / 7
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
